'use client';

import type { MiddlewareData, OpenChangeReason } from '@floating-ui/react';
import { useEffect } from 'react';

import { useCallbackRef } from './use-callback-ref';

export interface UseViewportLeaveClosePropsInterface {
  /** Whether viewport-leave closing is active, i.e. `shouldCloseOnViewportLeave`. */
  enabled: boolean;
  /** Whether Floating UI has completed positioning for the current open state. */
  isPositioned: boolean;
  /** Floating UI middleware data. `hide().referenceHidden` reports the trigger leaving the viewport. */
  middlewareData: MiddlewareData;
  /**
   * Open state handler, i.e. each component's `updateOpenState`. Always invoked as
   * `(false, undefined, 'ancestor-scroll')`, so handlers typing `event` as `Event` or
   * `SyntheticEvent` are both assignable.
   */
  onOpenChange: (open: boolean, event: undefined, reason: OpenChangeReason) => void;
  /** Current open state. */
  open: boolean | undefined;
}

/**
 * Closes a floating element once its trigger scrolls out of the viewport.
 *
 * Requires the `hide()` middleware to be registered on the same `useFloating` call, since
 * `hide().referenceHidden` is the signal this reads. Pass `open` to `useFloating` and
 * its `isPositioned` result here to ignore stale middleware data on reopen. Closes with Floating UI's
 * own `ancestor-scroll` reason so consumers can treat it like any other dismissal.
 *
 * @param props - See {@link UseViewportLeaveClosePropsInterface}.
 *
 * @example
 * useViewportLeaveClose({
 *   enabled: shouldCloseOnViewportLeave,
 *   isPositioned,
 *   middlewareData,
 *   onOpenChange: updateOpenState,
 *   open,
 * });
 */
export function useViewportLeaveClose({
  enabled,
  isPositioned,
  middlewareData,
  onOpenChange,
  open,
}: UseViewportLeaveClosePropsInterface): void {
  const handleOpenChange = useCallbackRef(onOpenChange);
  const isReferenceHidden = Boolean(middlewareData.hide?.referenceHidden);

  useEffect(() => {
    if (!enabled || !open || !isPositioned || !isReferenceHidden) return;

    handleOpenChange(false, undefined, 'ancestor-scroll');
  }, [enabled, handleOpenChange, isPositioned, isReferenceHidden, open]);
}
